import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { TrendingUp, Tag, Users, Headphones, LogOut, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import {
  InfluencerOverviewTab,
  InfluencerClientsTab,
  InfluencerCouponsTab,
  InfluencerBrowseTab,
  InfluencerAudiobookTab,
  InfluencerEditProfileDialog,
} from '../components/influencer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InfluencerDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [keys, setKeys] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [clientCoupons, setClientCoupons] = useState([]);
  const [audiobooks, setAudiobooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genPlan, setGenPlan] = useState('premium');
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [allCoupons, setAllCoupons] = useState([]);

  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [userRes, dashRes, keysRes, commRes, couponsRes, abRes, allCouponsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { headers }),
        axios.get(`${BACKEND_URL}/api/influencer/dashboard`, { headers }),
        axios.get(`${BACKEND_URL}/api/influencer/keys`, { headers }),
        axios.get(`${BACKEND_URL}/api/influencer/commissions`, { headers }),
        axios.get(`${BACKEND_URL}/api/influencer/client-coupons`, { headers }),
        axios.get(`${BACKEND_URL}/api/audiobooks/influencer`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/coupons`, { headers }).catch(() => ({ data: [] }))
      ]);
      setUser(userRes.data);
      setDashboard(dashRes.data);
      setKeys(keysRes.data);
      setCommissions(commRes.data);
      setClientCoupons(couponsRes.data);
      setAudiobooks(abRes.data);
      setAllCoupons(allCouponsRes.data);
      setEditForm({
        whatsapp: userRes.data.whatsapp || '',
        full_name: userRes.data.full_name || userRes.data.name || '',
        cpf: userRes.data.cpf || '',
        city: userRes.data.city || '',
        neighborhood: userRes.data.neighborhood || '',
        state: userRes.data.state || '',
        address: userRes.data.address || '',
        pix: userRes.data.pix || ''
      });
    } catch (error) {
      if (error.response?.status === 401) { localStorage.removeItem('session_token'); navigate('/'); }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateKey = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/influencer/keys`, { plan: genPlan }, { headers });
      toast.success(`Chave gerada: ${res.data.key}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar chave');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/auth/profile`, { ...editForm, role: 'influencer' }, { headers });
      toast.success('Perfil atualizado!');
      setShowEditProfile(false);
      fetchData();
    } catch {
      toast.error('Erro ao salvar perfil');
    }
  };

  const handleDeleteAccount = async () => {
    if (!confirm('Tem certeza que deseja excluir sua conta? Esta acao e irreversivel.')) return;
    if (!confirm('ULTIMA CONFIRMACAO: Excluir conta permanentemente?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/auth/account`, { headers });
      localStorage.removeItem('session_token');
      toast.success('Conta excluida com sucesso');
      navigate('/');
    } catch {
      toast.error('Erro ao excluir conta');
    }
  };

  const handleLogout = () => { localStorage.removeItem('session_token'); navigate('/'); };

  const handleShareClub = () => {
    const msg = `Conheça o ClubdoCupom! Encontre os melhores Descontos da sua Cidade. Rápido, fácil e na palma da sua mão!\n\nAcesse: www.clubdocupom.com`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Visao Geral', icon: TrendingUp },
    { id: 'clients', label: 'Atividades', icon: Users },
    { id: 'coupons', label: 'Cupons', icon: Tag },
    { id: 'browse', label: 'Cupons Disponiveis', icon: Tag },
    { id: 'audiobook', label: 'AudioBook', icon: Headphones },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {user?.picture && <img src={user.picture} alt="" className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white/30 shrink-0" />}
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold truncate">Painel do Influenciador</h1>
                <p className="text-xs text-indigo-200 truncate">{user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <Button size="sm" variant="ghost" onClick={() => setShowEditProfile(true)}
                className="text-white hover:bg-white/20 px-2 sm:px-3" data-testid="edit-profile-btn">
                <UserCog className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Editar</span>
              </Button>
              <Button size="sm" variant="ghost" onClick={handleLogout}
                className="text-white hover:bg-white/20 px-2 sm:px-3" data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="h-4 w-4" /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'overview' && (
          <InfluencerOverviewTab dashboard={dashboard} keys={keys} commissions={commissions}
            genPlan={genPlan} setGenPlan={setGenPlan} generating={generating}
            onGenerateKey={handleGenerateKey} onShareClub={handleShareClub} />
        )}
        {activeTab === 'clients' && <InfluencerClientsTab clientCoupons={clientCoupons} onRefresh={fetchData} />}
        {activeTab === 'coupons' && <InfluencerCouponsTab clientCoupons={clientCoupons} />}
        {activeTab === 'browse' && <InfluencerBrowseTab allCoupons={allCoupons} userCity={user?.city} navigate={navigate} />}
        {activeTab === 'audiobook' && <InfluencerAudiobookTab audiobooks={audiobooks} backendUrl={BACKEND_URL} />}
      </div>

      <InfluencerEditProfileDialog open={showEditProfile} onOpenChange={setShowEditProfile}
        editForm={editForm} setEditForm={setEditForm}
        onSave={handleSaveProfile} onDelete={handleDeleteAccount} />
    </div>
  );
}
